import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { extractError } from '@/api/client';
import { useCatalogueGroupes, useGererGroupes } from '@/hooks/useBetonCatalog';

/**
 * Groupes de catalogue (24/10/2026) : créer, renommer, désactiver, ordonner. Un groupe n'est jamais supprimé :
 * désactivé, il n'est plus proposé pour un produit, et ses produits restent dans sa section.
 */
export const GroupesCatalogueDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const q = useCatalogueGroupes();
  const { creer, modifier, ordonner } = useGererGroupes();
  const [noms, setNoms] = useState<Record<string, string>>({});
  const [nouveau, setNouveau] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const groupes = q.data?.groupes ?? [];

  useEffect(() => {
    if (open) { setNoms(Object.fromEntries(groupes.map((g) => [g.id, g.libelle]))); setErreur(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q.data]);

  const agir = async (f: () => Promise<unknown>) => {
    setErreur(null);
    try { await f(); } catch (e) { setErreur(extractError(e).message); }
  };
  const deplacer = (i: number, sens: -1 | 1) => {
    const ids = groupes.map((g) => g.id);
    [ids[i], ids[i + sens]] = [ids[i + sens], ids[i]];
    void agir(() => ordonner.mutateAsync(ids));
  };
  const occupe = creer.isPending || modifier.isPending || ordonner.isPending;

  return (
    <Dialog open={open} onClose={onClose} title="Groupes du catalogue" subtitle="Propres à cette société — un groupe désactivé n’est plus proposé" size="lg">
      <div className="space-y-3">
        {erreur && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{erreur}</div>}
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {groupes.map((g, i) => {
            const nom = noms[g.id] ?? g.libelle;
            const change = nom.trim() !== g.libelle && nom.trim() !== '';
            return (
              <li key={g.id} className={`flex flex-wrap items-center gap-2 px-3 py-2 ${g.actif ? '' : 'bg-gray-50'}`}>
                <div className="flex flex-col">
                  <button type="button" className="text-gray-400 hover:text-gray-700 disabled:opacity-30" disabled={i === 0 || occupe} aria-label={`Monter ${g.libelle}`} onClick={() => deplacer(i, -1)}><ArrowUp className="size-3.5" /></button>
                  <button type="button" className="text-gray-400 hover:text-gray-700 disabled:opacity-30" disabled={i === groupes.length - 1 || occupe} aria-label={`Descendre ${g.libelle}`} onClick={() => deplacer(i, 1)}><ArrowDown className="size-3.5" /></button>
                </div>
                <Input className="h-9 min-w-[160px] flex-1" value={nom} onChange={(e) => setNoms((n) => ({ ...n, [g.id]: e.target.value }))} aria-label={`Nom du groupe ${g.libelle}`} />
                <span className="w-20 text-right text-xs tabular-nums text-gray-500">{g.nbProduits} produit{g.nbProduits > 1 ? 's' : ''}</span>
                {change && (
                  <Button size="sm" variant="secondary" disabled={occupe} onClick={() => void agir(() => modifier.mutateAsync({ id: g.id, libelle: nom.trim() }))}>Renommer</Button>
                )}
                <Button size="sm" variant={g.actif ? 'ghost' : 'secondary'} disabled={occupe}
                  onClick={() => void agir(() => modifier.mutateAsync({ id: g.id, actif: !g.actif }))}>
                  {g.actif ? 'Désactiver' : 'Réactiver'}
                </Button>
              </li>
            );
          })}
          {groupes.length === 0 && <li className="px-3 py-4 text-center text-xs text-gray-500">Aucun groupe pour cette société</li>}
        </ul>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (nouveau.trim()) void agir(async () => { await creer.mutateAsync(nouveau.trim()); setNouveau(''); }); }}>
          <Input className="flex-1" value={nouveau} onChange={(e) => setNouveau(e.target.value)} placeholder="Nouveau groupe, par exemple « Pavés »" maxLength={80} />
          <Button type="submit" size="sm" disabled={occupe || !nouveau.trim()}><Plus className="size-4" /> Ajouter</Button>
        </form>
        <p className="text-[11px] text-gray-500">{q.data?.nbNonClasses ?? 0} produit(s) sans groupe, affichés dans « Non classés ».</p>
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose}>Fermer</Button></div>
      </div>
    </Dialog>
  );
};
